import { pageTitle } from '@palaccount/config';
import type { PriceTierResponse } from '@palaccount/contracts';
import { Check, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';
import { ConfirmBox, DeleteConfirmBox } from '@/components/confirm-box';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AuthorshipLine } from '@/features/master-data/authorship-line';
import { localizedName } from '@/lib/localized-name';
import { localizeServerMessage } from '@/lib/server-messages';

import { usePriceTierMutations, usePriceTiers } from './api';
import { InventoryTabs } from './inventory-tabs';

interface TierFormState {
  name: string;
  /** S5 / TIER-01: the optional Arabic twin of `name`. */
  nameAr: string;
  description: string;
  sortOrder: string;
  isDefault: boolean;
}

const EMPTY: TierFormState = {
  name: '',
  nameAr: '',
  description: '',
  sortOrder: '0',
  isDefault: false,
};

/**
 * MD-14 (owner ruling R5) — the tenant's own price bands: list + form. Any
 * number of tiers; the columns are the spec's own (§4): Name, Order,
 * Customers, Items priced, Default, Status.
 */
export function TiersPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<PriceTierResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<TierFormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(t('tiers.title'));
  }, [t]);

  const { data: tiers, isPending } = usePriceTiers(showInactive);
  const { create, update, setActive, moveCustomers, remove } = usePriceTierMutations();
  /** MD-14 §6 "Move customers to…" — the retire-a-tier action. */
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [movedNotice, setMovedNotice] = useState<string | null>(null);
  /** ERR-MD-14-05 — the switch-off question, asked while customers are on it. */
  const [confirmingOff, setConfirmingOff] = useState(false);
  /** ERR-MD-14-03 — field 5's "ticking a second unticks the first, AFTER ASKING". */
  const [confirmingDefault, setConfirmingDefault] = useState(false);
  /** BR-MD-14-08 — even an unused tier's delete asks first, through §4.20's box. */
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  /**
   * The tier that would be UNTICKED by making this one the default — the
   * "Standard" of ERR-MD-14-03's sentence. Excludes the record being edited:
   * re-ticking the default on the default itself is not "a second".
   */
  const currentDefault = (tiers ?? []).find(
    (tier) => tier.isDefault && tier.id !== (editing?.id ?? ''),
  );

  const closeDialogs = () => {
    setConfirmingOff(false);
    setConfirmingDefault(false);
    setConfirmingDelete(false);
  };
  const openNew = () => {
    setEditing(null);
    setValues(EMPTY);
    setError(null);
    closeDialogs();
    setFormOpen(true);
  };
  const openEdit = (tier: PriceTierResponse) => {
    setEditing(tier);
    setValues({
      name: tier.name,
      nameAr: tier.nameAr ?? '',
      description: tier.description ?? '',
      sortOrder: String(tier.sortOrder),
      isDefault: tier.isDefault,
    });
    setError(null);
    setMoveOpen(false);
    setMoveTargetId('');
    setMovedNotice(null);
    closeDialogs();
    setFormOpen(true);
  };

  /** The act itself, shared by the direct path and ERR-MD-14-05's "yes". */
  const doSetActive = () => {
    if (!editing) return;
    setActive
      .mutateAsync({ tierId: editing.id, active: !editing.isActive })
      .then(() => {
        setConfirmingOff(false);
        setFormOpen(false);
      })
      .catch((cause: unknown) => {
        setConfirmingOff(false);
        // The cause rides along (D46): a structured refusal carries
        // key+params, so the sentence reaches an Arabic reader in Arabic.
        setError(
          localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
        );
      });
  };

  const submitMove = async () => {
    if (!editing || moveTargetId === '') return;
    setError(null);
    try {
      const after = await moveCustomers.mutateAsync({
        tierId: editing.id,
        targetTierId: moveTargetId,
      });
      const target = (tiers ?? []).find((tier) => tier.id === moveTargetId);
      setEditing(after);
      setMoveOpen(false);
      setMoveTargetId('');
      setMovedNotice(t('tiers.movedNotice', { name: target?.name ?? '' }));
    } catch (cause) {
      // D46 — moveTargetInactive and moveNobody arrive structured here.
      setError(
        localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
      );
    }
  };

  const submit = async () => {
    setError(null);
    // ERR-MD-14-01.
    if (values.name.trim() === '') return setError(t('tiers.errName'));
    const nameAr = values.nameAr.trim();
    const body = {
      name: values.name.trim(),
      ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
      sortOrder: /^\d{1,3}$/.test(values.sortOrder.trim()) ? Number(values.sortOrder) : 0,
      isDefault: values.isDefault,
    };
    try {
      // An emptied Arabic box CLEARS the twin on edit (null); on create it is
      // simply not sent.
      if (editing) {
        await update.mutateAsync({
          tierId: editing.id,
          body: { ...body, nameAr: nameAr === '' ? null : nameAr },
        });
      } else {
        await create.mutateAsync({ ...body, ...(nameAr === '' ? {} : { nameAr }) });
      }
      setFormOpen(false);
    } catch (cause) {
      // D46 — nameTaken arrives structured here.
      setError(
        localizeServerMessage(t, cause instanceof Error ? cause.message : String(cause), cause),
      );
    }
  };

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('tiers.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createTiers">
          <Button onClick={openNew}>
            <Plus aria-hidden /> {t('common.new')}
          </Button>
        </Gated>
      </div>

      {formOpen ? (
        <Card>
          <CardHeader>
            <CardTitle>{t(editing ? 'tiers.editTitle' : 'tiers.newTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="grid gap-1.5">
                <Label htmlFor="t-name">{t('tiers.fName')}</Label>
                <Input
                  id="t-name"
                  maxLength={60}
                  value={values.name}
                  onChange={(event) => setValues({ ...values, name: event.target.value })}
                />
              </div>
              {/* S5 / TIER-01: the Arabic twin, beside the primary name. */}
              <div className="grid gap-1.5">
                <Label htmlFor="t-name-ar">{t('tiers.fNameAr')}</Label>
                <Input
                  id="t-name-ar"
                  dir="rtl"
                  lang="ar"
                  maxLength={60}
                  value={values.nameAr}
                  onChange={(event) => setValues({ ...values, nameAr: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="t-description">{t('tiers.fDescription')}</Label>
                <Input
                  id="t-description"
                  maxLength={200}
                  value={values.description}
                  onChange={(event) => setValues({ ...values, description: event.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="t-order">{t('tiers.fSortOrder')}</Label>
                <Input
                  id="t-order"
                  dir="ltr"
                  inputMode="numeric"
                  className="font-mono"
                  value={values.sortOrder}
                  onChange={(event) => setValues({ ...values, sortOrder: event.target.value })}
                />
              </div>
            </div>
            {/* BR-MD-14-03: ticking here unticks the previous default —
                "after asking" (field 5). ERR-MD-14-03 is that question:
                "Standard is already the default for new customers. Make
                Wholesale the default instead?" The tick lands only on yes;
                the SAVE then tells the server, which unticks the previous
                atomically either way. */}
            <Label className="flex items-center gap-2 font-normal">
              <Checkbox
                checked={values.isDefault}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  if (next && !values.isDefault && currentDefault !== undefined) {
                    setConfirmingDefault(true);
                    return;
                  }
                  setValues({ ...values, isDefault: next });
                }}
              />
              {t('tiers.fDefault')}
            </Label>
            <div className="flex gap-2">
              <Gated
                permission={editing ? 'inventory.edit' : 'inventory.create'}
                reasonKey={editing ? 'perm.cannot.editTiers' : 'perm.cannot.createTiers'}
              >
                <Button onClick={() => void submit()} disabled={!values.name.trim()}>
                  {t('common.save')}
                </Button>
              </Gated>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                {t('common.cancel')}
              </Button>
              {editing ? (
                <Gated permission="inventory.edit" reasonKey="perm.cannot.editTiers">
                <Button
                  variant="outline"
                  onClick={() => {
                    // ERR-MD-14-05: switching a tier off WITH CUSTOMERS ON IT
                    // asks first — they will fall back to the item's unit
                    // price. Nobody on it (the ERR's own trigger absent), or
                    // switching ON, acts directly.
                    if (editing.isActive && editing.customersOn > 0) {
                      setConfirmingOff(true);
                      return;
                    }
                    doSetActive();
                  }}
                >
                  {t(editing.isActive ? 'items.switchOff' : 'items.switchOn')}
                </Button>
                </Gated>
              ) : null}
              {/* BR-MD-14-08 with §6's offer rule: Delete renders ONLY while
                  nothing uses the tier ("hidden when any partner or any item
                  price uses the tier") — and still asks first, through the
                  shared §4.20 box below. A stale count is the server's to
                  refuse (ERR-MD-14-04, the RESTRICT FKs behind it). */}
              {editing && editing.customersOn === 0 && editing.itemsPricedAt === 0 ? (
                <Gated permission="inventory.delete" reasonKey="perm.cannot.deleteTiers">
                  <Button variant="outline" onClick={() => setConfirmingDelete(true)}>
                    {t('common.delete')}
                  </Button>
                </Gated>
              ) : null}
              {/* MD-14 §6: "Move customers to… — Administrator — Hidden when
                  the count is zero." One audited action retires the tier's
                  customers without visiting each record (the §13 Distributor
                  walkthrough). */}
              {editing && editing.customersOn > 0 ? (
                <Gated permission="inventory.approve" reasonKey="perm.cannot.moveTierCustomers">
                  <Button variant="outline" onClick={() => setMoveOpen((open) => !open)}>
                    {t('tiers.moveCustomers')}
                  </Button>
                </Gated>
              ) : null}
            </div>
            {movedNotice ? (
              <p className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                {movedNotice}
              </p>
            ) : null}
            {editing && moveOpen ? (
              <div className="flex flex-wrap items-end gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="t-move-target">{t('tiers.moveTarget')}</Label>
                  {/* §7: an inactive tier cannot take a new customer, so only
                      ACTIVE tiers (and never this one) are offered. */}
                  <NativeSelect
                    id="t-move-target"
                    value={moveTargetId}
                    onChange={(event) => setMoveTargetId(event.target.value)}
                  >
                    <option value="">—</option>
                    {(tiers ?? [])
                      .filter((tier) => tier.isActive && tier.id !== editing.id)
                      .map((tier) => (
                        <option key={tier.id} value={tier.id}>
                          {localizedName(tier, lang)}
                        </option>
                      ))}
                  </NativeSelect>
                </div>
                <Gated permission="inventory.approve" reasonKey="perm.cannot.moveTierCustomers">
                  <Button
                    onClick={() => void submitMove()}
                    disabled={moveTargetId === '' || moveCustomers.isPending}
                  >
                    {t('tiers.moveConfirm', { count: editing.customersOn })}
                  </Button>
                </Gated>
              </div>
            ) : null}
            {editing ? <AuthorshipLine record={editing} /> : null}
          </CardContent>
        </Card>
      ) : null}

      {/* ERR-MD-14-05 — "12 customers are on Wholesale. They will fall back
          to the item's unit price. Switch it off anyway?" Say yes (the verb),
          or move them first (§6's action, on the form behind this box). */}
      {editing !== null && confirmingOff ? (
        <ConfirmBox
          open
          title={t('tiers.confirmOffTitle')}
          body={t('tiers.confirmOffBody', { count: editing.customersOn, name: editing.name })}
          confirmLabel={t('items.switchOff')}
          onConfirm={doSetActive}
          onCancel={() => setConfirmingOff(false)}
          busy={setActive.isPending}
        />
      ) : null}

      {/* ERR-MD-14-03 — "Standard is already the default for new customers.
          Make Wholesale the default instead?" Yes ticks the box; the save is
          what unticks the previous, atomically, on the server (BR-MD-14-03). */}
      {confirmingDefault && currentDefault !== undefined ? (
        <ConfirmBox
          open
          title={t('tiers.defaultSwapTitle', {
            name: values.name.trim() || (editing?.name ?? ''),
          })}
          body={t('tiers.defaultSwapBody', { current: currentDefault.name })}
          confirmLabel={t('tiers.makeDefault')}
          onConfirm={() => {
            setValues({ ...values, isDefault: true });
            setConfirmingDefault(false);
          }}
          onCancel={() => setConfirmingDefault(false)}
        />
      ) : null}

      {/* BR-MD-14-08 — "Deleting a tier that nothing uses must still ask
          first, through the shared confirm dialog in §4.20." GEN-58's
          sentence comes from the shared box; the detail is this screen's
          what-will-happen half (GEN-205). */}
      {editing !== null ? (
        <DeleteConfirmBox
          open={confirmingDelete}
          detail={t('tiers.confirmDelete', { name: editing.name })}
          onConfirm={() => {
            remove
              .mutateAsync({ tierId: editing.id })
              .then(() => {
                setConfirmingDelete(false);
                setFormOpen(false);
                setEditing(null);
              })
              .catch((cause: unknown) => {
                setConfirmingDelete(false);
                // D46 — ERR-MD-14-04's both-counts refusal arrives structured.
                setError(
                  localizeServerMessage(
                    t,
                    cause instanceof Error ? cause.message : String(cause),
                    cause,
                  ),
                );
              });
          }}
          onCancel={() => setConfirmingDelete(false)}
          busy={remove.isPending}
        />
      ) : null}

      <Card>
        <CardContent className="grid gap-4">
          <Label className="flex items-center gap-2 text-sm font-normal">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {t('common.showInactive')}
          </Label>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : (tiers ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('tiers.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tiers.colName')}</TableHead>
                  <TableHead className="text-end">{t('tiers.colOrder')}</TableHead>
                  <TableHead className="text-end">{t('tiers.colCustomers')}</TableHead>
                  <TableHead className="text-end">{t('tiers.colItemsPriced')}</TableHead>
                  <TableHead>{t('tiers.colDefault')}</TableHead>
                  <TableHead>{t('tiers.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(tiers ?? []).map((tier) => (
                  <TableRow key={tier.id} className="cursor-pointer" onClick={() => openEdit(tier)}>
                    <TableCell>
                      <div className="grid">
                        <span className="font-medium">{localizedName(tier, lang)}</span>
                        <Ltr value={tier.code} className="font-mono text-xs text-muted-foreground" />
                      </div>
                    </TableCell>
                    <TableCell className="text-end font-mono">{tier.sortOrder}</TableCell>
                    <TableCell className="text-end font-mono">{tier.customersOn}</TableCell>
                    <TableCell className="text-end font-mono">{tier.itemsPricedAt}</TableCell>
                    <TableCell>
                      {tier.isDefault ? <Check className="size-4" aria-label={t('common.yes')} /> : '—'}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {t(tier.isActive ? 'common.active' : 'common.inactive')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
